import { useEffect, useMemo, useRef, useState } from 'react'
import { AddGrantsPopup } from './components/AddGrantsPopup'
import { graphqlRequestClient, queryClient } from '../../../queries/client'
import CompanyCard from './components/CompanyCard'
import { EsopSummaryCards } from './components/EsopSummaryCards'
import { EsopEmptyState } from './components/EsopEmptyState'
import {
  useGetGrantsQuery,
  Grant,
  useAddEoiMutation,
  useGetEoiForAllUsersQuery, 
} from '../../../generated/graphql'
import { getUserId } from '../../../utils/SessionStorageUtils'
import { FMVWarningBox } from './components/FMVWarningBox'
import { GrantCalculatorDrawer } from './components/GrantCalculatorDrawer'
import { BreadCrumbs } from '../company/components/Breadcrumbs'
import { KTSVG } from '../../../_metronic/helpers'
import ESOPFinancingDrawer from './components/ESOPFinancing'
import Toast, { ToastType } from '../../../utils/toast'

export function EsopPageWrapper() {
  const userId = getUserId()
  const { data, isLoading: isLoadingGrants } = useGetGrantsQuery(
    graphqlRequestClient,
    {},
    { enabled: !!userId }
  )

  const [grants, setGrants] = useState<Grant[]>([])
  const [showAddGrantPopup, setShowAddGrantPopup] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedGrant, setEditGrant] = useState<any>(null)
  const [showCalculator, setShowCalculator] = useState(false)
  const [calculatorGrant, setCalculatorGrant] = useState<any>(null)
  const [showFinancingDrawer, setShowFinancingDrawer] = useState(false)

  const companyCardRef = useRef<HTMLDivElement>(null)
  const addEoiMutation = useAddEoiMutation(graphqlRequestClient, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: useGetEoiForAllUsersQuery.getKey() })
      
      Toast('ESOP Financing request submitted successfully!', ToastType.success)
      setShowFinancingDrawer(false)
    },
    onError: (error: Error) => {
      Toast(
        error.message || 'Failed to submit financing request. Please try again.',
        ToastType.error
      )
      console.error('ESOP Financing submission error:', error)
    },
  })

  const isSubmitting = addEoiMutation.isLoading
  const submitError = addEoiMutation.error
    ? (addEoiMutation.error as any)?.message ||
      (addEoiMutation.error as any)?.response?.errors?.[0]?.message ||
      'Something went wrong. Please try again.'
    : null

  useEffect(() => {
    if (data?.grants) {
      const filteredGrants = data.grants.filter((g): g is Grant => g !== null)
      setGrants(filteredGrants)
    }
  }, [data])

  const handleCloseAddGrant = () => setShowAddGrantPopup(false)

  const handleOpenEditModal = (grantData: any) => {
    setEditGrant(grantData)
    setShowEditModal(true)
  }

  const handleOpenCalculator = (grantData: any) => {
    setCalculatorGrant(grantData)
    setShowCalculator(true)
  }

  const hasMissingFMV = grants.some((grant) => !grant.companyFMV || grant.companyFMV === 0)

  const grantsByCompany = useMemo(() => {
    const map: Record<string, Grant[]> = {}
    grants.forEach((grant) => {
      const company = grant.companyName || 'Unknown Company'
      if (!map[company]) map[company] = []
      map[company].push(grant)
    })
    return map
  }, [grants])

  const scrollToCompanyCard = () => {
    companyCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  const handleFinancingSubmit = async (data: { esopFinancing: any, companyName: string }) => {
    await addEoiMutation.mutateAsync({
      input: {
        esopFinancing: data.esopFinancing,
        companyName: data.companyName,
        offerType: 'ESOP_FINANCING',
      },
    })
  }
  if (isLoadingGrants) {
    return (
      <div className='w-100 my-20 d-flex justify-content-center align-items-center'>
        <span className='spinner-grow'></span>
      </div>
    )
  }

  if (grants.length === 0) {
    return (
      <div className='container'>
        <EsopEmptyState onAddGrant={() => setShowAddGrantPopup(true)} />
        {showAddGrantPopup && (
          <AddGrantsPopup
            showModal={showAddGrantPopup}
            handleClose={handleCloseAddGrant}
            userId={userId!}
          />
        )}
      </div>
    )
  }

  return (
    <>
      <div className='row mb-9'>
        <BreadCrumbs label={'Grants'} isActionItems={false} className='mt-2' />
      </div>

      {hasMissingFMV && (
        <div className='row mb-4'>
          <div className='col-12 mx-auto'>
            <FMVWarningBox grants={grants} onFixClick={scrollToCompanyCard} />
          </div>
        </div>
      )}

      <div className='row'>
        <div className='col-12'>
          <EsopSummaryCards grants={grants} onClickAdd={scrollToCompanyCard} />
        </div>
      </div>

      <div className='row mb-9' ref={companyCardRef}>
        <div className='col-12'>
          <div className='card p-0 card border border-gray-300' style={{ border: '1px solid #F1F1F2' }}>
            <div className='card-header d-flex flex-wrap flex-md-nowrap align-items-center justify-content-between bg-white border-bottom-0 px-4 px-md-6 py-4 py-md-5'>
              <div className='d-flex flex-column ms-6 mt-6'>
                <div className='d-flex align-items-center gap-2 mb-2'>
                  <h3 className='fw-bold mb-0'>Grant Portfolio Details</h3>
                </div>
                <div className='text-gray-700 fs-7'>
                  Track and manage your equity grants across companies
                </div>
              </div>

              <div className='d-flex align-items-center gap-3 mt-3 mt-md-0'>
                <span
                  className='badge text-dark fw-bold px-3 py-2 border border rounded-pill'
                  style={{ fontSize: '0.75rem' }}
                >
                  {grants.length} grant{grants.length !== 1 ? 's' : ''}
                </span>
                <button
                  className='px-4 rounded border border-primary bg-primary h-38px fw-bold ms-4 text-white d-flex align-items-center gap-2'
                  onClick={() => setShowAddGrantPopup(true)}
                >
                  <span className='me-2 fs-4'>+</span> Add Grant
                </button>
              </div>
            </div>
            <hr className='my-1 border-gray-600' />

            <div className='card-body pt-0 px-0 pb-0'>
              {Object.entries(grantsByCompany).map(([companyName, companyGrants]) => {
                const grantsCount = companyGrants.length
                const vestedOptions = companyGrants.reduce((sum, g) => sum + (g.vestedOptions ?? 0), 0)
                const vestedValue = companyGrants.reduce(
                  (sum, g) => sum + (g.vestedOptions ?? 0) * (g.companyFMV ?? 0),
                  0
                )
                const unexercisedValue = companyGrants
                  .reduce(
                    (sum, g) =>
                      sum + ((g.numberGranted ?? 0) - (g.numberExercised ?? 0)) * (g.companyFMV ?? 0),
                    0
                  )
                  .toLocaleString('en-IN')

                return (
                  <CompanyCard
                    key={companyName}
                    companyName={companyName}
                    grantsCount={grantsCount}
                    vestedOptions={vestedOptions}
                    vestedValue={vestedValue}
                    unexercisedValue={unexercisedValue}
                    grants={companyGrants}
                    openEditModal={handleOpenEditModal}
                    onOpenCalculator={handleOpenCalculator}
                  />
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Modals & Drawers */}
      {showAddGrantPopup && (
        <AddGrantsPopup
          showModal={showAddGrantPopup}
          handleClose={handleCloseAddGrant}
          userId={userId!}
        />
      )}

      {showEditModal && selectedGrant && (
        <AddGrantsPopup
          showModal={showEditModal}
          handleClose={() => {
            setShowEditModal(false)
            setEditGrant(null)
          }}
          userId={userId!}
          grantData={selectedGrant}
          onGrantCreated={() => {
            setShowEditModal(false)
            setEditGrant(null)
          }}
          isEdit={true}
        />
      )}

      {/* ESOP Financing Drawer */}
      <ESOPFinancingDrawer
        isOpen={showFinancingDrawer}
        onClose={() => setShowFinancingDrawer(false)}
        onConfirm={(data: any)=>handleFinancingSubmit(data)}
        isSubmitting={isSubmitting}
        submitError={submitError}
        grants={grants}
      />

      {showCalculator && calculatorGrant && (
        <GrantCalculatorDrawer
          isOpen={showCalculator}
          onClose={() => {
            setShowCalculator(false)
            setCalculatorGrant(null)
          }}
          grant={calculatorGrant}
        />
      )}
    </>
  )
}